import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  MENTAL_PATTERN_ORDER,
  MENTAL_PATTERN_LABELS,
  MENTAL_PATTERN_COLORS,
  MENTAL_PATTERN_INTENT,
  MENTAL_PATTERN_HELP
} from "../../constants/mentalPatterns.js";
import { HUB_ILLUSTRATIONS, MENTAL_PATTERN_ILLUSTRATIONS } from "../../constants/mediaAssets.js";
import HubIllustration from "../HubIllustration.js";
import MentalPatternsBackLink from "./MentalPatternsBackLink.js";
import LockedPatternTease from "./LockedPatternTease.js";
import { useMentalPatterns } from "./MentalPatternsContext.js";
import { MIN_INSIGHTS_FOR_PATTERNS } from "../../constants/mentalPatternsHub.js";

const MentalPatternsUnderStress = () => {
  const { isSubscriber, verifiedEmail } = useOutletContext() || {};
  const { stressAnalysis, totalReflections, lockedPatternTease } = useMentalPatterns();
  const [stressHistoryOpen, setStressHistoryOpen] = useState(false);

  const {
    stressInsights,
    distribution,
    totalStress,
    weeklyTrend,
    maxWeekly,
    attentionPattern,
    patternsWithData
  } = stressAnalysis;

  const pieKeys = patternsWithData.length ? patternsWithData : MENTAL_PATTERN_ORDER;
  const pieTotal = totalStress || 1;
  const showLockedTease =
    !isSubscriber && totalReflections >= MIN_INSIGHTS_FOR_PATTERNS && totalStress > 0;

  if (totalStress === 0) {
    return (
      <>
        <MentalPatternsBackLink />
        <div className="mental-patterns__hero">
          <HubIllustration src={HUB_ILLUSTRATIONS.underStress} alt="" className="hub-illustration hub-illustration--stress" />
          <h1>Under stress</h1>
        </div>
        <p>
          No under-stress reflections found yet. Patterns appear when reflections show stress-related emotions or
          language (overwhelm, anxiety, pressure, burnout, and similar).
        </p>
      </>
    );
  }

  if (showLockedTease) {
    return (
      <>
        <MentalPatternsBackLink />
        <div className="mental-patterns__hero">
          <HubIllustration src={HUB_ILLUSTRATIONS.underStress} alt="" className="hub-illustration hub-illustration--stress" />
          <h1>Under stress</h1>
        </div>
        <LockedPatternTease tease={lockedPatternTease} verifiedEmail={verifiedEmail} />
      </>
    );
  }

  return (
    <>
      <MentalPatternsBackLink />
      <div className="mental-patterns__hero">
        <HubIllustration src={HUB_ILLUSTRATIONS.underStress} alt="" className="hub-illustration hub-illustration--stress" />
        <h1>Under stress</h1>
      </div>

      {attentionPattern ? (
        <section className="mental-patterns__attention" aria-label="Pattern needing attention">
          <h2>Needs your attention now</h2>
          <div className="mental-patterns__attention-card">
            <HubIllustration
              src={MENTAL_PATTERN_ILLUSTRATIONS[attentionPattern]}
              alt=""
              className="hub-illustration hub-illustration--pattern mental-patterns__attention-art"
            />
            <span
              className="mental-patterns__attention-swatch"
              style={{ background: MENTAL_PATTERN_COLORS[attentionPattern] }}
              aria-hidden
            />
            <div>
              <p className="mental-patterns__attention-name">{MENTAL_PATTERN_LABELS[attentionPattern]}</p>
              <p className="mental-patterns__attention-intent">
                Trying to: {MENTAL_PATTERN_INTENT[attentionPattern]}
              </p>
              <p className="mental-patterns__attention-help">
                <strong>What helps:</strong> {MENTAL_PATTERN_HELP[attentionPattern]}
              </p>
            </div>
          </div>
          <p className="mental-patterns__attention-note">
            Based on recent under-stress reflections (last 2 weeks weighted more heavily).
          </p>
        </section>
      ) : null}

      <section className="mental-patterns__trend" aria-label="Under stress over time">
        <h2>Under stress over time</h2>
        <p className="mental-patterns__total">
          {totalStress} under-stress reflection{totalStress !== 1 ? "s" : ""} across {weeklyTrend.length} week
          {weeklyTrend.length !== 1 ? "s" : ""}
        </p>
        <div className="mental-patterns__trend-chart" role="img" aria-label="Weekly under-stress reflection counts">
          {weeklyTrend.map((week) => {
            const heightPct = Math.round((week.count / maxWeekly) * 100);
            return (
              <div key={week.weekKey} className="mental-patterns__trend-col">
                <div className="mental-patterns__trend-bar-wrap">
                  <div
                    className="mental-patterns__trend-bar"
                    style={{ height: `${Math.max(heightPct, 8)}%` }}
                    title={`${week.count} reflection${week.count !== 1 ? "s" : ""}`}
                  />
                </div>
                <span className="mental-patterns__trend-count">{week.count}</span>
                <span className="mental-patterns__trend-label">{week.label}</span>
              </div>
            );
          })}
        </div>
      </section>

      <section className="mood-distribution mental-patterns__distribution" aria-label="Pattern distribution">
        <h2>Stress-response distribution</h2>
        <div className="mood-distribution__pie-wrap">
          <div
            className="mood-pie"
            role="img"
            aria-label={`Pattern distribution: ${pieKeys
              .map((k) => `${MENTAL_PATTERN_LABELS[k]} ${distribution[k] || 0}`)
              .join(", ")}`}
            style={{
              background: (() => {
                if (!totalStress) return "none";
                let cumulative = 0;
                const parts = pieKeys.map((key) => {
                  const count = distribution[key] || 0;
                  const pct = (count / pieTotal) * 100;
                  const start = cumulative;
                  cumulative += pct;
                  return `${MENTAL_PATTERN_COLORS[key]} ${start}% ${cumulative}%`;
                });
                return parts.length ? `conic-gradient(${parts.join(", ")})` : "rgba(139, 115, 85, 0.25)";
              })()
            }}
          />
        </div>
        <div className="mood-pie-legend">
          {pieKeys.map((key) => {
            const count = distribution[key] || 0;
            const pct = totalStress ? (count / totalStress) * 100 : 0;
            return (
              <div key={key} className="mood-pie-legend__item">
                <HubIllustration
                  src={MENTAL_PATTERN_ILLUSTRATIONS[key]}
                  alt=""
                  className="hub-illustration hub-illustration--pattern mood-pie-legend__illus"
                />
                <span className="mood-pie-legend__swatch" style={{ background: MENTAL_PATTERN_COLORS[key] }} />
                <span className="mood-pie-legend__label">{MENTAL_PATTERN_LABELS[key]}</span>
                <span className="mood-pie-legend__count">
                  {count} ({pct.toFixed(0)}%)
                </span>
              </div>
            );
          })}
        </div>
      </section>

      <section className="mental-patterns__history mental-patterns__collapsible" aria-label="Under-stress reflections">
        <button
          type="button"
          className="mental-patterns__collapsible-trigger"
          onClick={() => setStressHistoryOpen((open) => !open)}
          aria-expanded={stressHistoryOpen}
          aria-controls="under-stress-reflections-list"
        >
          <h2>Under-stress reflections</h2>
          <span className="mental-patterns__collapsible-count">{stressInsights.length}</span>
          <span className="mental-patterns__collapsible-icon" aria-hidden>
            {stressHistoryOpen ? "−" : "+"}
          </span>
        </button>
        {stressHistoryOpen ? (
          <ul id="under-stress-reflections-list" className="mental-patterns__history-list">
            {stressInsights.map((entry) => (
              <li key={entry.id} className="mental-patterns__history-item">
                <HubIllustration
                  src={MENTAL_PATTERN_ILLUSTRATIONS[entry.pattern]}
                  alt=""
                  className="hub-illustration hub-illustration--pattern mental-patterns__history-art"
                />
                <span
                  className="mental-patterns__history-swatch"
                  style={{ background: MENTAL_PATTERN_COLORS[entry.pattern] }}
                  aria-hidden
                />
                <div>
                  <p className="mental-patterns__history-meta">
                    {new Date(entry.date).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric"
                    })}
                    {" · "}
                    {MENTAL_PATTERN_LABELS[entry.pattern]}
                    {entry.emotion ? ` · ${entry.emotion}` : ""}
                  </p>
                  {entry.preview ? (
                    <p className="mental-patterns__history-preview">{entry.preview}…</p>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    </>
  );
};

export default MentalPatternsUnderStress;
